import React, { useEffect, useState } from "react";
import Assignee from "components/react-select/assignees";
import { Button } from "components/button";
import { Input } from "components/input";
import axios from "axios";
import { useQueryClient } from "react-query";
import { format } from "date-fns";
import { useModalContext } from "components/modal";
import { useTranslation } from 'react-i18next';
import { convertFromUTC, convertToUTC } from "utils/utcConversion";
import { useAuthContext } from "contexts/auth";

const EditBlock = (props) => {
  const queryClient = useQueryClient();
  const { setModal } = useModalContext();
  const { t } = useTranslation();
  const tBase = "views.calendar.editBlock";
  const tr = (key) => t(`${tBase}.${key}`);  
  const { timezone } = useAuthContext();
  const [errorMessage, setErrorMessage] = useState("");

  const formatDateYyyyMmDd = (date) => {
    if (!date) return "";

  return String(date).split("T")[0];
  };

  const [loaded, setLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [description, setDescription] = useState("");




  const fetchBlock = async () => {
    const res = await axios.get(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/company/time_block/getTimeBlockById`,
      {
        params: {
          blockId: props.block.id,
        },
      }
    );

    const { start_time, end_time, start_time_utc, end_time_utc } = res.data;
    const startTimeUTC = start_time_utc ? convertFromUTC(start_time_utc, timezone, false) : start_time.substring(0, 5);
    const endTimeUTC = end_time_utc ? convertFromUTC(end_time_utc, timezone, false) : end_time.substring(0, 5);

    if (res.status === 200) {
      setAssignees(res.data.assignees);
      setStartDate(formatDateYyyyMmDd(res.data.start_date));
      setEndDate(formatDateYyyyMmDd(res.data.end_date));
      setStartTime(startTimeUTC);
      setEndTime(endTimeUTC);
      setDescription(res.data.description);
      setLoaded(true);
    }
  };

  useEffect(() => {
    fetchBlock();
  }, []);

  if (!loaded) return null;

  const updateTimeBlock = async (values) => {
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/time_block/updateTimeBlock`,
        { values }
      );

      if (res.status === 200) {
        setIsSubmitting(false);
        setModal(null);
        // Invalidate queries to trigger data refresh
        await queryClient.refetchQueries("appointments");
        await queryClient.refetchQueries("appointment");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = () => {
    setErrorMessage("");
    if (
      props.block.id &&
      assignees &&
      assignees.length > 0 &&
      startDate &&
      endDate &&
      startTime &&
      endTime &&
      description
    ) {
      if (endDate > startDate && endTime <= startTime) {
        setErrorMessage(tr("For multi-day blocks, end time must be after start time"));
        return;
      }

      setIsSubmitting(true);

      const values = {
        blockId: props.block.id,
        assignees,
        startDate,
        endDate,
        // startTime: format(new Date(startDate + " " + startTime), "HH:mmXXX"),
        // endTime: format(new Date(endDate + " " + endTime), "HH:mmXXX"),
        startTime: convertToUTC(startDate, startTime, timezone),
        endTime: convertToUTC(startDate, endTime, timezone),
        description,
      };

      updateTimeBlock(values);
    } else {
      setErrorMessage(tr("All fields are required."));
    }
  };

  const handleDelete = async () => {
    setIsSubmitting(true);
    try {
      const res = await axios.delete(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/time_block/deleteTimeBlock`,
        {
          data: {
            blockId: props.block.id,
          },
        }
      );

      if (res.status === 200) {
        await queryClient.refetchQueries("appointments");
        await queryClient.refetchQueries("appointment");
        setModal(null);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <Assignee
        value={assignees}
        onChange={setAssignees}
        assigneeType="appointment"
        label={tr("Assignee")}
      />

      <Input
        value={startDate}
        onChange={(e) => {
          setStartDate(e.target.value);
          setErrorMessage("");
        }}
        label={tr("Start Date")}
        type="date"
        max="9999-12-31"
      />

      <Input
        value={endDate}
        onChange={(e) => {
          setEndDate(e.target.value);
          setErrorMessage("");
        }}
        label={tr("End Date")}
        type="date"
      />

      <Input
        value={startTime}
        onChange={(e) => {
          setStartTime(e.target.value);
          setErrorMessage("");
        }}
        label={tr("Start Time")}
        type="time"
      />

      <Input
        value={endTime}
        onChange={(e) => {
          setEndTime(e.target.value);
          setErrorMessage("");
        }}
        label={tr("End Time")}
        type="time"
        min={startDate === endDate ? startTime || undefined : undefined}
      />

      <Input
        value={description}
        onChange={(e) => {
          setDescription(e.target.value);
        }}
        label={tr("Description")}
        type="text"
      />

      <div
        style={{
          display: "flex",
          flex: 1,
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Button
          style={{ marginTop: 10, marginRight: 10 }}
          disabled={isSubmitting}
          onClick={handleDelete}
        >
          {tr("Delete")}
        </Button>
        <div className="flex space-x-4">
          {errorMessage && (
            <div className="text-red-500 mt-5">{errorMessage}</div>
          )}
        <Button
          style={{ marginTop: 10 }}
          disabled={isSubmitting}
          onClick={handleSubmit}
        >
          {tr("Save")}
        </Button>
        </div>
      </div>
    </div>
  );
};

export default EditBlock;